import { useRef, useState } from "react";
import { Eye, Download, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { qgApi, errorMessage } from "@/lib/qgApi";

// Preview / Download / Print all fetch the same saved revision (or saved draft) PDF from the backend.
export function PdfActions({ quotationId, fileName, revision, beforeFetch, compact = false, testPrefix = "qg-pdf" }) {
  const [busy, setBusy] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const printFrame = useRef(null);

  const fetchPdf = async (kind) => {
    setBusy(kind);
    try {
      if (beforeFetch) await beforeFetch();
      const blob = await qgApi.pdfBlob(quotationId, revision);
      return URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    } catch (e) {
      let msg = errorMessage(e, "Could not generate the PDF");
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      }
      toast.error(typeof msg === "string" ? msg : "Could not generate the PDF");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const preview = async () => { const url = await fetchPdf("preview"); if (url) setPreviewUrl(url); };
  const download = async () => {
    const url = await fetchPdf("download");
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName || "quotation"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("PDF downloaded");
  };
  const print = async () => {
    const url = await fetchPdf("print");
    if (!url || !printFrame.current) return;
    printFrame.current.onload = () => { try { printFrame.current.contentWindow.focus(); printFrame.current.contentWindow.print(); } catch { window.open(url, "_blank"); } };
    printFrame.current.src = url;
  };

  const btn = (kind, Icon, label, onClick) => (
    <Button type="button" variant="outline" size={compact ? "sm" : "default"} onClick={onClick} disabled={!!busy}
      data-testid={`${testPrefix}-${kind}-btn`} className="rounded-lg border-slate-300 dark:border-slate-600" title={label}>
      {busy === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
      {!compact && <span className="ml-1.5">{label}</span>}
    </Button>
  );

  return (
    <div className="flex items-center gap-2">
      {btn("preview", Eye, "Preview PDF", preview)}
      {btn("download", Download, "Download", download)}
      {btn("print", Printer, "Print", print)}
      <iframe ref={printFrame} title="print-frame" className="hidden" />
      <Dialog open={!!previewUrl} onOpenChange={(o) => !o && setPreviewUrl(null)}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-4" data-testid={`${testPrefix}-preview-dialog`}>
          <DialogHeader><DialogTitle>{fileName}</DialogTitle></DialogHeader>
          {previewUrl && <iframe src={previewUrl} title="PDF preview" className="flex-1 w-full rounded-md border border-slate-200" data-testid={`${testPrefix}-preview-frame`} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
